import * as React from "react";
import { motion } from "framer-motion";
import AddToCalendar from "@culturehq/add-to-calendar";
import "@culturehq/add-to-calendar/dist/styles.css";
import { IParts } from "@src/components/Parts";
import DialogModal from "../../Dialog";
import LiveSchedules from "../../LiveSchedules";
import formatDateWithOrdinal, {
  formatEventTime,
} from "@src/utils/dateFormatter";
import { useEventParts } from "@src/hooks/useEventParts";

const Parts: React.FC<IParts> = ({
  parts,
  eventPartsData,
  liveSchedules,
  eventTimezone,
  isInvitedToEvent,
}) => {
  const { parts: derivedParts } = useEventParts({
    eventPartsData,
    liveSchedules,
    eventTimezone,
    isInvitedToEvent,
  });
  const visibleParts = parts ?? derivedParts;
  const schedules =
    liveSchedules ??
    ({
      getLiveSchedule: {
        __typename: "PaginatedLiveSchedule",
        page: 1,
        pageSize: 0,
        pageCount: 0,
        recordCount: 0,
        data: [],
      },
    } as NonNullable<typeof liveSchedules>);

  const addOneDay = (dateTime: string) => {
    const date = new Date(dateTime);
    date.setHours(date.getHours() + 24);
    return date.toISOString();
  };

  return visibleParts.length > 0 ? (
    <section className="relative w-full overflow-hidden bg-[#FAF7F5] px-6 py-10 md:px-10 md:py-16">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_8%,rgba(34,139,34,0.08),transparent_30%),radial-gradient(circle_at_82%_18%,rgba(28,24,32,0.05),transparent_28%)]" />

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={{
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: { delayChildren: 0.1, staggerChildren: 0.1 },
          },
        }}
        className="relative mx-auto max-w-5xl"
      >
        <div className="mb-12 text-center md:mb-16">
          <span className="mb-4 block font-jost text-[0.62rem] font-medium uppercase tracking-[0.35em] text-[#228B22]">
            Schedule
          </span>
          <h2 className="font-corGaramond text-[clamp(3rem,7vw,5.4rem)] font-normal leading-[1.02] text-[#401d15]">
            Event <em className="font-light italic text-[#228B22]">Details</em>
          </h2>
          <div className="mt-5 flex items-center justify-center gap-4">
            <span className="h-px w-[56px] bg-gradient-to-r from-transparent to-[#228B22]" />
            <span className="h-1.5 w-1.5 rotate-45 bg-[#228B22]" />
            <span className="h-px w-[56px] bg-gradient-to-r from-[#228B22] to-transparent" />
          </div>
        </div>

        <div className="border-y border-[#228B22]/12">
          {visibleParts.map((item, idx) => (
            <motion.article
              key={`event-${idx}`}
              variants={{
                hidden: { opacity: 0, y: 24 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.55 } },
              }}
              className="grid gap-6 border-b border-[#228B22]/10 py-9 last:border-b-0 md:grid-cols-[72px_1fr_auto] md:gap-8"
            >
              <div className="font-corGaramond text-5xl font-light leading-none text-[#228B22]/20">
                {String(idx + 1).padStart(2, "0")}
              </div>

              <div>
                <h3 className="mb-2 font-corGaramond text-3xl font-semibold leading-tight text-[#401d15] md:text-4xl">
                  {item.name}
                </h3>
                <p className="mb-3 font-jost text-[0.68rem] font-medium uppercase tracking-[0.16em] text-[#228B22]">
                  {formatDateWithOrdinal(
                    item.time,
                    item.eventPartTimezone ?? eventTimezone
                  ).trim()}{" "}
                  &nbsp;·&nbsp;
                  {formatEventTime(
                    item.time,
                    item.eventPartTimezone ?? eventTimezone
                  ).trim()}
                </p>
                {item.showAddress && (
                  <p className="max-w-2xl font-corGaramond text-xl font-light leading-[1.55] text-[#401d15]/60">
                    {item.address}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-3 md:justify-end">
                <div className="inline-flex min-h-[42px] items-center justify-center border border-[#401d15] px-5 font-jost text-[0.58rem] font-normal uppercase tracking-[0.2em] text-[#401d15] transition hover:bg-[#401d15] hover:text-white group">
                  <AddToCalendar
                    event={{
                      name: item.name,
                      details: null,
                      location: item?.address,
                      startsAt: item?.time,
                      endsAt: addOneDay(item?.time),
                    }}
                  >
                    <span className="uppercase group-hover:text-white">
                      Save Date
                    </span>
                  </AddToCalendar>
                </div>
                <DialogModal
                  fillScreen={true}
                  trigger={
                    <button className="min-h-[42px] border border-[#401d15] px-5 font-jost text-[0.58rem] font-normal uppercase tracking-[0.2em] text-[#401d15] transition hover:bg-[#401d15] hover:text-white">
                      Watch Live
                    </button>
                  }
                >
                  <LiveSchedules schedules={schedules} />
                </DialogModal>
              </div>
            </motion.article>
          ))}
        </div>
      </motion.div>
    </section>
  ) : null;
};

export default Parts;
